// ── MeetingMap 결과 입력 · 킵 · 저장 액션 훅 ────────────────────────────────
// - openResultModal: 결과 입력 모달 열기 (유효기간 인코딩 복원 포함)
// - toggleKeep: 킵 즉시 토글
// - fetchKeepList: 킵목록 전체 조회
// - saveResult: 결과 저장 (feedback + contract + keep 동시 처리)

import { apiClient } from '../../../api/apiClient';
import { HQ_MEETING_API } from '../meetingMapApi';

export function useMeetingMapActions(core, {
  adminMode,
  api = HQ_MEETING_API,   // 관리자 주소 묶음(2026-10-22)
  showToast,
  fetchMeetings,
  fetchPendingFeedback,
  fetchMeetingRefs,
  fetchContractTicketTypes,
  extractValidity,
  parseContractTicketString,
}) {
  const {
    visitDate,
    savingResult, setSavingResult,
    resultForm, setResultForm,
    setOriginalIsKeep,
    setResultOpen,
    keepLoading, setKeepLoading,
    setKeepList, setKeepListLoading,
    setMeetings,
    contractTicketTypes, externalCategories,
    originalIsKeep,
  } = core;

  // ── 결과 입력 모달 열기 ─────────────────────────────────────────────────────
  function openResultModal(item) {
    // 참조 데이터(피드백 옵션·인계 담당자) 최신화 — await 없이 발사만 (모달은 즉시 열리고
    //   응답 도착 시 선택지가 제자리 갱신됨). 장시간 켜둔 세션에서도 설정 변경이 반영되게 한다.
    if (fetchMeetingRefs) fetchMeetingRefs();
    const isContracted = Boolean(item.is_contracted);
    const contractItemsArr = Array.isArray(item.contract_items) ? item.contract_items : [];

    let selectedProducts, productCounts, productValidities;

    if (isContracted && contractItemsArr.length > 0) {
      // 신규 포맷: contract_items 배열로 폼 상태 초기화
      const normal = contractItemsArr.filter((i) => !i.isService);
      productCounts    = {};
      productValidities = {};
      selectedProducts = normal.map((i) => {
        const name = i.ticketTypeName || '';
        if (i.quantity != null)      productCounts[name]    = String(i.quantity);
        if (i.validityMonths != null) productValidities[name] = String(i.validityMonths);
        return name;
      });
    } else {
      // 하위 호환 또는 미계약 레코드: 기존 문자열 파싱
      const ticketSrc      = isContracted ? (item.contract_ticket_type || item.ticket_type) : item.ticket_type;
      const ticketCountSrc = isContracted ? null : item.ticket_count;
      const { selectedProducts: rawProds, productCounts: rawCounts } = parseContractTicketString(ticketSrc, ticketCountSrc);

      productValidities = {};
      productCounts     = {};
      selectedProducts  = rawProds.map((rawName) => {
        const { name: baseName, validity } = extractValidity(rawName);
        if (validity) productValidities[baseName] = validity;
        productCounts[baseName] = rawCounts[rawName] || rawCounts[baseName] || '';
        // 비내부 카테고리명 → 기본 상품명으로 치환 (예: '영화티켓' → '동반할인권')
        const cat = externalCategories.find((c) => c.name === baseName);
        if (cat?.default_product_name) {
          const product = contractTicketTypes.find((t) => t.name === cat.default_product_name);
          if (product?.default_validity_months != null && !productValidities[cat.default_product_name]) {
            productValidities[cat.default_product_name] = String(product.default_validity_months);
          }
          productCounts[cat.default_product_name] = productCounts[baseName] || '';
          return cat.default_product_name;
        }
        return baseName;
      });
    }

    setOriginalIsKeep(Boolean(item.is_keep));
    setResultForm({
      id: item.id,
      company_name: item.company_name || '', // audit log 검색용 + putBody에 null 방지
      // 계약완료 레코드는 feedback 초기화 — 둘 다 세팅된 경우 방지
      feedback: isContracted ? '' : (item.feedback || ''),
      feedback_memo: isContracted ? '' : (item.feedback_memo || ''),
      is_contracted: isContracted,
      is_keep: Boolean(item.is_keep),
      contract_date: item.contract_date || '',
      ticket_type: item.ticket_type || '',
      ticket_count: item.ticket_count != null ? String(item.ticket_count) : '',
      selectedProducts,
      productCounts,
      movieTicketSubType: '',
      productValidities,
      new_sales_person_name: '',
      new_visit_date: '',
    });
    // 이미 계약 상태로 모달이 열릴 때도 fetchContractTicketTypes 호출
    if (isContracted) fetchContractTicketTypes();
    setResultOpen(true);
  }

  // ── 킵목록 즉시 토글 (저장 버튼과 독립적으로 동작) ────────────────────────
  async function toggleKeep() {
    if (!resultForm.id || keepLoading) return;
    // 마케터는 등록만 — 이미 킵된 상태에서 해제 불가
    if (!adminMode && resultForm.is_keep) {
      showToast('킵 해제는 TM직원에게 문의해 주세요.', 'info'); return;
    }
    const next = !resultForm.is_keep;
    setKeepLoading(true);
    const apiBasePath = adminMode ? api.base : '/api/tm/meeting-map';
    const res = await apiClient.patch(`${apiBasePath}/${resultForm.id}/keep`, { is_keep: next });
    setKeepLoading(false);
    if (!res.ok) { showToast('킵 처리에 실패했습니다.', 'error'); return; }
    // 킵 ON 시: 방문일/담당자 필드 초기화
    setResultForm((prev) => ({
      ...prev,
      is_keep: next,
      ...(next ? { new_visit_date: '', new_sales_person_name: '' } : {}),
    }));
    // 미팅 목록에도 즉시 반영
    setMeetings((prev) =>
      prev.map((m) => Number(m.id) === Number(resultForm.id) ? { ...m, is_keep: next } : m)
    );
    showToast(next ? '킵목록에 추가되었습니다.' : '킵목록에서 제거되었습니다.', 'success');
  }

  // ── 킵목록 전체 조회 ────────────────────────────────────────────────────────
  async function fetchKeepList() {
    setKeepListLoading(true);
    const apiBasePath = adminMode ? api.base : '/api/tm/meeting-map';
    const res = await apiClient.get(`${apiBasePath}/keep`);
    setKeepListLoading(false);
    if (res.ok) setKeepList(Array.isArray(res.data?.data) ? res.data.data : []);
  }

  // ── 결과 저장 (feedback + contract + keep 동시 처리) ───────────────────────
  async function saveResult() {
    if (!resultForm.id || savingResult) return;
    setSavingResult(true);

    // 멀티선택 상품 → contract_items 배열로 직렬화
    const selProducts = resultForm.selectedProducts || [];
    let finalTicketType, finalTicketCount;
    let contractItemsPayload = [];

    if (selProducts.length > 0) {
      // contract_items 배열 구성 (ticket_type_id 조회 필요)
      contractItemsPayload = selProducts.flatMap((name) => {
        const tt  = contractTicketTypes.find((t) => t.name === name);
        const cnt = Number((resultForm.productCounts || {})[name] || 0);
        const v   = (resultForm.productValidities || {})[name] || '';
        if (!tt?.id || cnt <= 0) return [];
        return [{ ticket_type_id: tt.id, validity_months: v ? Number(v) : null, quantity: cnt, is_service: false }];
      });
      // 하위 호환용 문자열 (calls.contract_ticket_type 컬럼 동시 업데이트)
      const parts = selProducts.map((name) => {
        const cnt = String((resultForm.productCounts || {})[name] || '').trim();
        const v   = (resultForm.productValidities || {})[name] || '';
        const finalName = v ? `${name}(${v}개월)` : name;
        return cnt ? `${finalName}×${cnt}` : finalName;
      });
      finalTicketType  = parts.join(', ');
      const total = selProducts.reduce((sum, name) => {
        const cnt = Number((resultForm.productCounts || {})[name] || 0);
        return sum + (isNaN(cnt) ? 0 : cnt);
      }, 0);
      finalTicketCount = total > 0 ? total : null;
    } else {
      finalTicketType  = String(resultForm.ticket_type || '').trim() || null;
      finalTicketCount = String(resultForm.ticket_count || '').trim()
        ? Number(resultForm.ticket_count) : null;
    }

    const putBody = {
      is_contracted: Boolean(resultForm.is_contracted),
      contract_date: resultForm.contract_date || null,
      company_name: resultForm.company_name || null, // audit log 내용 검색용
    };
    if (resultForm.is_contracted) {
      // 계약완료 시: contract_items 배열로 저장 (API가 contract_items 테이블에 삽입)
      putBody.contract_items = contractItemsPayload;
      // calls.contract_ticket_type 컬럼도 하위 호환을 위해 업데이트
      putBody.contract_ticket_type = finalTicketType || null;
    } else {
      putBody.ticket_type  = finalTicketType;
      putBody.ticket_count = finalTicketCount;
    }
    // 미뤄짐: 새 방문일자 포함
    if (resultForm.feedback === '미뤄짐' && resultForm.new_visit_date) {
      putBody.visit_date = resultForm.new_visit_date;
    }
    // 미팅인계: 새 마케팅 담당자 포함
    if (resultForm.feedback === '미팅인계' && resultForm.new_sales_person_name) {
      putBody.sales_person_name = resultForm.new_sales_person_name;
    }

    const apiBasePath = adminMode ? api.base : '/api/tm/meeting-map';
    // is_keep이 변경된 경우에만 PATCH 호출 (마케터는 이미 킵된 것 해제 불가)
    const isKeepChanged = resultForm.is_keep !== originalIsKeep;
    const canChangeKeep = adminMode || !originalIsKeep;

    // ── PUT 먼저 완료 후 PATCH 실행 (순차 처리) ─────────────────────────────
    // 미팅인계 시 PUT이 sales_person_id를 바꾸고, 동시에 PATCH가 권한 체크를 하면
    // 변경된 sales_person_id로 인해 원 담당자가 403을 받는 경쟁 조건 방지
    const putRes = await apiClient.put(`${apiBasePath}/${resultForm.id}`, putBody);
    setSavingResult(false);
    if (!putRes.ok) { showToast(putRes.data?.message || '결과 저장에 실패했습니다.', 'error'); return; }

    const feedbackBody = {
      // 계약완료 시 feedback 강제 null
      feedback: resultForm.is_contracted
        ? null
        : (String(resultForm.feedback || '').trim() || null),
      feedback_memo: resultForm.is_contracted
        ? null
        : (String(resultForm.feedback_memo || '').trim() || null),
      company_name: resultForm.company_name || null,
    };
    const patchRequests = [
      apiClient.patch(`${apiBasePath}/${resultForm.id}/feedback`, feedbackBody),
      ...(isKeepChanged && canChangeKeep
        ? [apiClient.patch(`${apiBasePath}/${resultForm.id}/keep`, {
            is_keep: resultForm.is_keep,
            company_name: resultForm.company_name || null,
          })]
        : []),
    ];
    const [patchRes, keepRes] = await Promise.all(patchRequests);
    if (!patchRes.ok) { showToast(patchRes.data?.message || '피드백 저장에 실패했습니다.', 'error'); return; }
    if (keepRes && !keepRes.ok) { showToast('킵 처리에 실패했습니다.', 'error'); return; }

    showToast(resultForm.is_contracted ? '계약이 접수되었습니다.' : '미팅 결과가 저장되었습니다.', 'success');
    setResultOpen(false);
    fetchMeetings(visitDate);
    fetchPendingFeedback(); // 피드백 미입력 뱃지 카운트 갱신
  }

  return { openResultModal, toggleKeep, fetchKeepList, saveResult };
}
